import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FaCheckCircle, 
  FaBox, 
  FaTruck, 
  FaMapMarkerAlt, 
  FaCreditCard,
  FaDownload,
  FaHome,
  FaShoppingBag,
  FaFileInvoice,
  FaGift,
  FaTicketAlt
} from "react-icons/fa";
import Invoice from "../Invoice";
import { useInvoiceModal } from "../../contexts/InvoiceModalContext";

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setInvoiceModalOpen } = useInvoiceModal();
  const [order, setOrder] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);

  const openInvoice = () => {
    setShowInvoice(true);
    setInvoiceModalOpen(true);
  };

  const closeInvoice = () => {
    setShowInvoice(false);
    setInvoiceModalOpen(false);
  };

  useEffect(() => {
    // Prefer a full order object passed via location.state (from payment flow)
    const orderFromState = location.state?.order;

    if (orderFromState) {
      setOrder(orderFromState);
      return;
    }

    try {
      const savedCheckoutOrder = sessionStorage.getItem('lastCheckoutOrder');
      if (savedCheckoutOrder) {
        const parsed = JSON.parse(savedCheckoutOrder);
        sessionStorage.removeItem('lastCheckoutOrder');
        if (parsed && typeof parsed === 'object') {
          setOrder(parsed);
          return;
        }
      }
    } catch (_) {}

    // Otherwise try to resolve by orderId in query or state
    const orderId = new URLSearchParams(location.search).get('orderId') || location.state?.orderId;

    const resolveOrder = async () => {
      if (orderId) {
        // Try localStorage first
        const savedOrders = JSON.parse(localStorage.getItem('orders') || '[]');
        const foundOrder = savedOrders.find(o => o.id === orderId);

        if (foundOrder) {
          setOrder(foundOrder);
          return;
        }

        // Fallback: attempt to fetch order from API by ID (if backend available)
        try {
          const res = await fetch(`/api/orders/${orderId}`);
          if (res.ok) {
            const data = await res.json();
            setOrder(data);
            return;
          }
        } catch (e) {
          // ignore fetch errors and fall through to redirect
          // console.error('Order fetch failed', e);
        }
        navigate('/myorder');
      } else {
        navigate('/myorder');
      }
    };

    resolveOrder();
  }, [location, navigate]);

  // Format date
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Get estimated delivery date
  const getEstimatedDelivery = useCallback(() => {
    if (!order) return '';
    const date = new Date(order.date);
    date.setDate(date.getDate() + 7);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  }, [order]);

  // Order Timeline Steps Component
  const TimelineStep = React.memo(({ icon: Icon, title, description, date, isActive, isCompleted }) => (
    <div className="flex gap-2 md:gap-4">
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center shadow-lg ${
          isCompleted ? 'bg-green-500' : isActive ? 'bg-blue-500' : 'bg-gray-300'
        }`}>
          <Icon className={`text-sm md:text-xl ${
            isCompleted || isActive ? 'text-white' : 'text-gray-500'
          }`} />
        </div>
        {title !== 'Delivered' && (
          <div className={`w-0.5 h-12 md:h-16 mt-2 ${
            isCompleted ? 'bg-green-500' : 'bg-gray-300'
          }`}></div>
        )}
      </div>
      <div className="flex-1 pb-4 md:pb-6">
        <h3 className={`font-semibold mb-1 text-sm md:text-base ${
          isCompleted || isActive ? 'text-gray-800' : 'text-gray-400'
        }`}>
          {title}
        </h3>
        <p className={`text-xs md:text-sm mb-1 ${
          isCompleted || isActive ? 'text-gray-600' : 'text-gray-500'
        }`}>
          {description}
        </p>
        {date && (
          <p className="text-[10px] md:text-xs text-gray-500">{date}</p>
        )}
      </div>
    </div>
  ));

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-b-2 border-blue-600 mx-auto mb-3 md:mb-4"></div>
          <p className="text-gray-600 text-sm md:text-base">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Print Styles - Hide Bottom Navigation */}
      <style>{`
        @media print {
          /* Hide bottom navigation - multiple selectors for reliability */
          nav[class*="fixed"][class*="bottom-0"],
          nav.fixed.bottom-0,
          nav[class*="bottom-0"][class*="left-0"],
          nav[class*="BottomNavigation"],
          .bottom-navigation,
          footer[class*="fixed"],
          footer[class*="bottom"],
          header[class*="fixed"][class*="bottom"] {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          
          /* Remove body padding for bottom nav */
          body {
            padding-bottom: 0 !important;
            margin-bottom: 0 !important;
          }
          
          /* Hide action buttons when printing */
          button[onclick*="window.print"],
          button[onclick*="print"],
          a[href="/myorder"],
          a[href="/"],
          a[href*="/myorder"],
          a[href*="/"]:not([href*="mailto"]):not([href*="tel"]) {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Hide print:hidden elements */
          .print\\:hidden,
          [class*="print:hidden"] {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Ensure content is visible and properly sized */
          .min-h-screen {
            min-height: auto !important;
          }
          
          /* Remove background gradients for better printing */
          .bg-gradient-to-br,
          .bg-gradient-to-r {
            background: white !important;
          }
          
          /* Ensure proper page breaks */
          .bg-white {
            page-break-inside: avoid;
          }
          
          /* Remove shadows for cleaner print */
          .shadow-xl,
          .shadow-lg,
          .shadow-md,
          .shadow-sm {
            box-shadow: none !important;
          }
          /* Invoice modal: when printing, show only invoice content without overlay */
          .invoice-modal-wrap {
            position: static !important;
            background: transparent !important;
            padding: 0 !important;
            align-items: flex-start !important;
          }
          .invoice-modal-wrap .bg-white {
            max-height: none !important;
            box-shadow: none !important;
          }
        }
      `}</style>
      <div className="checkout-light-page min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-4 md:py-8">
      <div className="max-w-4xl mx-auto px-3 md:px-4">
        {/* Success Animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="text-center mb-4 md:mb-8"
        >
          <div className="relative inline-block">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-16 h-16 md:w-24 md:h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 shadow-2xl"
            >
              <FaCheckCircle className="text-white text-3xl md:text-5xl" />
            </motion.div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="absolute inset-0 bg-green-500 rounded-full opacity-20"
            />
          </div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-1 md:mb-2"
          >
            Order Confirmed!
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-sm md:text-lg text-gray-600 mb-1 md:mb-2"
          >
            Thank you for your purchase
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-xs md:text-sm text-gray-500"
          >
            Order ID: <span className="font-semibold text-gray-700">{order.id}</span>
          </motion.p>

          {/* Lucky Draw entry banner */}
          {order.luckyDrawTickets && Array.isArray(order.luckyDrawTickets) && order.luckyDrawTickets.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-4 md:mb-6"
            >
              <Link
                to="/lucky-draw"
                className="flex items-center gap-4 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 md:p-5 hover:from-emerald-100 hover:to-teal-100 hover:border-emerald-300 transition-colors duration-200"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <FaGift className="text-white text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm md:text-base">
                    You're entered in the Lucky Draw
                  </p>
                  <p className="text-xs md:text-sm text-gray-600 mt-0.5">
                    {order.luckyDrawTickets.length === 1
                      ? order.luckyDrawTickets[0].drawTitle
                      : `${order.luckyDrawTickets.reduce((s, t) => s + (t.quantity || 0), 0)} ticket(s) in ${order.luckyDrawTickets.length} draw(s)`}
                  </p>
                  <p className="text-xs text-emerald-700 font-medium mt-1.5">
                    View my tickets →
                  </p>
                </div>
                <FaTicketAlt className="text-emerald-500 text-lg flex-shrink-0" />
              </Link>
            </motion.div>
          )}
        </motion.div>

        {/* Lucky Draw Details - what user bought */}
        {order.luckyDrawTickets && Array.isArray(order.luckyDrawTickets) && order.luckyDrawTickets.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="bg-white rounded-xl md:rounded-2xl shadow-xl p-4 md:p-6 mb-4 md:mb-6 border border-gray-100"
          >
            <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
              <FaTicketAlt className="text-emerald-600" />
              Lucky Draw Details
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 font-semibold text-gray-700">Draw</th>
                    <th className="text-center py-2 font-semibold text-gray-700">Qty</th>
                    <th className="text-right py-2 font-semibold text-gray-700">Price</th>
                    <th className="text-right py-2 font-semibold text-gray-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {order.luckyDrawTickets.map((t, idx) => {
                    const amt = (Number(t.ticketPrice) || 0) * (Number(t.quantity) || 0);
                    return (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="py-2 text-gray-900 font-medium">{t.drawTitle || 'Lucky Draw'}</td>
                        <td className="py-2 text-center text-gray-700">{t.quantity || 0}</td>
                        <td className="py-2 text-right text-gray-700">₹{(Number(t.ticketPrice) || 0).toFixed(2)}</td>
                        <td className="py-2 text-right font-semibold text-gray-900">₹{amt.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200">
                    <td colSpan={3} className="py-2 text-right font-semibold text-gray-700">Lucky Draw Total</td>
                    <td className="py-2 text-right font-bold text-gray-900">
                      ₹{order.luckyDrawTickets.reduce((s, t) => s + (Number(t.ticketPrice) || 0) * (Number(t.quantity) || 0), 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </motion.div>
        )}

        {/* Order Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl md:rounded-2xl shadow-xl p-4 md:p-6 mb-4 md:mb-6 border border-gray-100"
        >
          <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4">Order Summary</h2>
          <div className="flex flex-wrap gap-2 md:gap-3">
            {order.items?.slice(0, 3).map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2 flex-shrink-0 max-w-[140px] md:max-w-[180px]">
                {item.imageUrl && (
                  <img loading="lazy" src={item.imageUrl} alt={item.name} className="w-10 h-10 md:w-12 md:h-12 object-cover rounded" />
                )}
                <span className="text-xs md:text-sm text-gray-800 truncate">{item.name}</span>
              </div>
            ))}
          </div>
          {order.items?.length > 3 && (
            <p className="text-sm text-gray-600 text-center pt-2">
              + {order.items.length - 3} more item{order.items.length - 3 > 1 ? 's' : ''}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="bg-white rounded-xl md:rounded-2xl shadow-xl p-4 md:p-6 mb-4 md:mb-6 border border-gray-100"
        >
          {/* Order Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 mb-4 md:mb-6">
            {/* Shipping Address */}
            {order.shippingAddress && (
              <div className="bg-gray-50 rounded-lg p-3 md:p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FaMapMarkerAlt className="text-blue-600 text-sm md:text-base" />
                  <h3 className="font-semibold text-gray-800 text-sm md:text-base">Delivery Address</h3>
                </div>
                <p className="text-xs md:text-sm text-gray-600">
                  {order.shippingAddress.name}
                </p>
                <p className="text-xs md:text-sm text-gray-600">
                  {order.shippingAddress.address}
                </p>
                <p className="text-xs md:text-sm text-gray-600">
                  {order.shippingAddress.cityState}, {order.shippingAddress.postalCode}
                </p>
              </div>
            )}

            {/* Payment Info */}
            <div className="bg-gray-50 rounded-lg p-3 md:p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaCreditCard className="text-green-600 text-sm md:text-base" />
                <h3 className="font-semibold text-gray-800 text-sm md:text-base">Payment</h3>
              </div>
              <p className="text-xs md:text-sm text-gray-600 mb-1">
                Method: <span className="font-medium">{order.paymentMethod}</span>
              </p>
              <p className="text-xs md:text-sm text-gray-600">
                Status: <span className={`font-medium ${order.paymentStatus === 'Paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {order.paymentStatus}
                </span>
              </p>
              <p className="text-base md:text-lg font-bold text-gray-800 mt-2">
                ₹{order.total.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Estimated Delivery */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <FaTruck className="text-blue-600 text-base md:text-xl" />
              <div>
                <p className="text-xs md:text-sm text-gray-600">Estimated Delivery</p>
                <p className="text-base md:text-lg font-semibold text-blue-800">
                  {getEstimatedDelivery()}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Order Tracking Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl md:rounded-2xl shadow-xl p-4 md:p-6 lg:p-8 mb-4 md:mb-6 border border-gray-100"
        >
          <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">Order Tracking</h2>
          
          <div className="relative">
            <div className="space-y-4 md:space-y-6">
              <TimelineStep
                icon={FaCheckCircle}
                title="Order Placed"
                description="Your order has been successfully placed and confirmed"
                date={formatDate(order.date)}
                isCompleted={true}
              />
              <TimelineStep
                icon={FaBox}
                title="Processing Item"
                description="We are processing your items for shipment"
                date={formatDate(new Date(Date.now() + 86400000).toISOString())}
                isActive={true}
              />
              <TimelineStep
                icon={FaBox}
                title="Packed"
                description="Your order has been packed and ready to ship"
                isActive={false}
              />
              <TimelineStep
                icon={FaTruck}
                title="Handed to Courier"
                description="Your package has been handed to the courier"
                isActive={false}
              />
              <TimelineStep
                icon={FaTruck}
                title="Shipment In Transit"
                description="Your shipment is on its way to you"
                isActive={false}
              />
              <TimelineStep
                icon={FaCheckCircle}
                title="Delivery Completed"
                description="Your order has been delivered to your address"
                isActive={false}
              />
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-2 md:gap-4 justify-center"
        >
          <Link
            to="/myorder"
            className="print:hidden flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg text-sm md:text-base"
          >
            <FaShoppingBag className="text-sm md:text-base" />
            View All Orders
          </Link>
          <Link
            to="/"
            className="print:hidden flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-white text-blue-600 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold shadow-lg text-sm md:text-base"
          >
            <FaHome className="text-sm md:text-base" />
            Continue Shopping
          </Link>
          <button
            onClick={openInvoice}
            className="print:hidden flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold shadow-lg text-sm md:text-base"
          >
            <FaFileInvoice className="text-sm md:text-base" />
            View Invoice
          </button>
        </motion.div>

        {/* Support Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="print:hidden text-center mt-4 md:mt-8"
        >
          <p className="text-xs md:text-sm text-gray-600 mb-2">
            Need help? Check our{" "}
            <Link to="/support" className="text-blue-600 hover:underline font-medium">
              Support Center
            </Link>
            {" "}or{" "}
            <Link to="/faq" className="text-blue-600 hover:underline font-medium">
              FAQs
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Invoice Modal - fixed height, scroll inside */}
      {showInvoice && order && (
        <div className="invoice-modal-wrap fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-3 md:p-4 overflow-hidden">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh] my-auto">
            <div className="flex-1 overflow-y-auto min-h-0">
              <Invoice
                order={order}
                onClose={closeInvoice}
              />
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default OrderConfirmation;
